import { Fragment } from "react";

import { ProviderRegistrationGenderDisclosureInner, ProviderRegistrationGenderDisclosureInnerTypeEnum } from "@asd/customer-portal-api";
import { Autocomplete, Checkbox, Divider, FormControlLabel, Stack, TextField, Typography } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";

import AddressPicker from "~/components/shared/AddressPicker";
import { GENDER_OPTIONS, LANGUAGES, PROVIDER_REGISTRATION_TITLE_PROPS } from "~/utils/providerRegistrationHelpers";

const Demographics = () => {
  const { control, register } = useFormContext();

  return (
    <Stack direction="column" spacing={4}>
      <Stack direction="column" spacing={2}>
        <Typography align="left" {...PROVIDER_REGISTRATION_TITLE_PROPS}>
          Enter your address:
        </Typography>
        <Controller
          control={control}
          defaultValue=""
          name="demographics.address"
          render={({ field, fieldState: { error } }) => (
            <AddressPicker
              error={!!error}
              helperText={error?.message}
              label="Address"
              onValueChange={field.onChange}
              sx={{
                width: "100%",
                "&.MuiAutocomplete-root .MuiInputBase-root": {
                  pt: "12px",
                  pb: "10px",
                },
                "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                  transform: "translate(14px, 14px) scale(1)",
                },
              }}
              value={field.value}
              required
            />
          )}
          rules={{ required: "Address is required" }}
        />
      </Stack>
      <Stack direction="column" spacing={2}>
        <Typography align="left" {...PROVIDER_REGISTRATION_TITLE_PROPS}>
          What is your gender?
        </Typography>
        <Controller
          control={control}
          defaultValue={[]}
          name="demographics.genderDisclosure"
          render={({ field, fieldState: { error } }) => (
            <>
              {GENDER_OPTIONS.map(([enumValue, label]) => {
                const existing = field.value.find((value: ProviderRegistrationGenderDisclosureInner) => value.type === enumValue);
                const isString = typeof existing?.selfDescription === "string";

                const declineKey = ProviderRegistrationGenderDisclosureInnerTypeEnum.no_answer;
                const isDeclineSelected = field.value.some((value: ProviderRegistrationGenderDisclosureInner) => value.type === declineKey);
                const isAnyNonDeclineSelected = field.value.some(
                  (value: ProviderRegistrationGenderDisclosureInner) => value.type !== declineKey,
                );
                const isThisDecline = enumValue === declineKey;
                const isDisabled = (isDeclineSelected && !isThisDecline) || (isAnyNonDeclineSelected && isThisDecline);

                const isSelfDescribed = enumValue === ProviderRegistrationGenderDisclosureInnerTypeEnum.self_described;

                return (
                  <Stack key={enumValue} direction="row">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!existing || isString}
                          disabled={isDisabled}
                          onChange={(e) => {
                            if (!e.target.checked) {
                              field.onChange(
                                field.value.filter((value: ProviderRegistrationGenderDisclosureInner) => value.type !== enumValue),
                              );
                            } else {
                              field.onChange([
                                ...field.value,
                                isSelfDescribed ? { type: enumValue, selfDescription: "" } : { type: enumValue },
                              ]);
                            }
                          }}
                        />
                      }
                      label={isSelfDescribed ? "" : label}
                      sx={[
                        isString && {
                          height: (theme) => `calc(${theme.spacing(7)} + 2px)`,
                        },
                      ]}
                    />
                    {isSelfDescribed && (
                      <TextField
                        disabled={isDisabled || !isString}
                        error={!isDisabled && isString && !!error}
                        helperText={!isDisabled && isString && error ? error.message : null}
                        label="Prefer to self-describe"
                        onChange={(e) => {
                          field.onChange(
                            field.value.map((value: ProviderRegistrationGenderDisclosureInner) =>
                              value.type === enumValue ? { ...value, selfDescription: e.target.value } : value,
                            ),
                          );
                        }}
                        required={isString}
                        value={existing?.selfDescription || ""}
                      />
                    )}
                  </Stack>
                );
              })}
            </>
          )}
          rules={{
            validate: (value) => {
              const selfDescribed = value?.find(
                (value: ProviderRegistrationGenderDisclosureInner) =>
                  value.type === ProviderRegistrationGenderDisclosureInnerTypeEnum.self_described,
              );
              if (selfDescribed && !selfDescribed.selfDescription?.length) {
                return "Value is required if checked";
              }
              return true;
            },
          }}
        />
      </Stack>
      <Stack direction="column" spacing={2}>
        <Typography align="left" {...PROVIDER_REGISTRATION_TITLE_PROPS}>
          Please select which languages you speak:
        </Typography>
        <Controller
          control={control}
          defaultValue={[]}
          name="demographics.languages"
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              {...register("demographics.languages", {
                required: "Language is required",
              })}
              {...field}
              getOptionLabel={({ name }) => name}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              onChange={(_e, data) => {
                field.onChange(data);
              }}
              options={LANGUAGES}
              renderInput={(params) => (
                <TextField {...params} error={!!error} helperText={error ? error.message : null} label="Language" required />
              )}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;

                return (
                  <Fragment key={key}>
                    <li {...optionProps}>
                      <FormControlLabel
                        control={<Checkbox checked={!!field.value.find(({ code }: { code: string }) => code === option.code)} />}
                        label={option.name}
                        slotProps={{
                          ...(option.code === "en" && {
                            typography: { sx: { fontWeight: "bold" } },
                          }),
                        }}
                      />
                    </li>
                    {option.code === "en" && <Divider />}
                  </Fragment>
                );
              }}
              value={field.value || []}
              disableCloseOnSelect
              multiple
            />
          )}
        />
      </Stack>
    </Stack>
  );
};

export default Demographics;
